import React, { useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Wallet,
  Package,
  Boxes,
  CircleCheck,
  Clock,
  CalendarClock,
  ChevronDown,
  MessageCircle,
  UserRound,
  MapPin,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { localCity } from "@/lib/storyTranslations";
import { gsap, useGsap } from "@/lib/motion";

const INTL_LOCALE = { ar: "ar-u-nu-latn", en: "en-GB", tr: "tr-TR", "zh-CN": "zh-CN" };
const WHATSAPP = "905521627900";
const GROUPS_COLLAPSED = 5;

/* Payment status → pill colours, on the card's dark forest surface. */
const STATUS_TONE = {
  paid: "bg-[oklch(0.9_0.19_122/0.14)] text-[var(--signal)] border-[oklch(0.9_0.19_122/0.35)]",
  partial: "bg-[oklch(0.8_0.14_75/0.14)] text-[oklch(0.88_0.13_80)] border-[oklch(0.8_0.14_75/0.4)]",
  unpaid: "bg-[oklch(0.65_0.17_28/0.16)] text-[oklch(0.84_0.1_28)] border-[oklch(0.7_0.15_28/0.45)]",
  pending: "bg-white/[0.06] text-[var(--on-dark-muted)] border-white/15",
  awaiting_confirmation: "bg-[oklch(0.75_0.1_230/0.14)] text-[oklch(0.86_0.07_230)] border-[oklch(0.75_0.1_230/0.4)]",
  cancelled: "bg-white/[0.06] text-[var(--on-dark-muted)] border-white/15",
};
const DUE_TONE = { partial: "text-[oklch(0.88_0.13_80)]", unpaid: "text-[oklch(0.84_0.1_28)]" };
const BAR_TONE = { partial: "bg-[oklch(0.84_0.14_80)]", unpaid: "bg-[oklch(0.75_0.15_28)]" };

function useMoney(lang) {
  return useMemo(() => {
    const loc = INTL_LOCALE[lang] || "en-GB";
    const num = new Intl.NumberFormat(loc, { maximumFractionDigits: 2 });
    return (amount, currency) => {
      const v = num.format(Number(amount) || 0);
      const code = (currency || "USD").toUpperCase();
      return SYMBOLS[code] ? `${SYMBOLS[code]}${v}` : `${v} ${code}`;
    };
  }, [lang]);
}

/* Two initials for Latin names ("AK"); one letter for Arabic, where two joined
   letters read as a word (أحمد خليل → «أخ», "brother"). */
const initials = (name) => {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  // A code entered as a name ("MSK-0505") gets the person icon, not "M".
  if (!words.length || /\d/.test(words[0])) return "";
  if (/[؀-ۿ]/.test(words[0])) return words[0][0];
  return words.slice(0, 2).map((w) => w[0]).join("").toUpperCase();
};

/* Keep an amount's own direction inside a sentence ("دُفع $300 من $845"). */
const iso = (s) => `⁦${s}⁩`;
/* "$545" in every language (Intl gives "US$" in Arabic); currencies without an
   unambiguous symbol read as "545 SYP". */
const SYMBOLS = { USD: "$", EUR: "€", TRY: "₺", GBP: "£", CNY: "¥" };

function Person({ label, name, sub }) {
  return (
    <div className="td-in flex items-center gap-3 min-w-0">
      <span
        aria-hidden
        className="shrink-0 w-11 h-11 rounded-full grid place-items-center bg-[oklch(0.37_0.078_138)] text-[var(--on-dark)] font-bold text-sm"
      >
        {initials(name) || <UserRound className="w-5 h-5" />}
      </span>
      <div className="min-w-0">
        <div className="text-xs text-[var(--on-dark-muted)]">{label}</div>
        <div className="font-bold text-[var(--on-dark)] truncate" title={name}>
          {name}
        </div>
        {sub && (
          <div className="text-xs text-[var(--on-dark-muted)] flex items-center gap-1 mt-0.5">
            <MapPin className="w-3 h-3" aria-hidden /> {sub}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TrackDetails({ shipment }) {
  const { t, lang } = useLang();
  const m = t.track.more;
  const d = shipment.details;
  const isRtl = lang === "ar";
  const money = useMoney(lang);
  const root = useRef(null);
  const [showAll, setShowAll] = useState(false);

  useGsap(
    () => {
      gsap.from(".td-in", { opacity: 0, y: 14, duration: 0.6, stagger: 0.06, delay: 0.9 });
      gsap.fromTo(".td-bar", { scaleX: 0 }, { scaleX: 1, duration: 1.1, ease: "expo.out", delay: 1.2 });
    },
    [shipment.trackingNumber, lang],
    root,
  );

  if (!d || !m) return null;

  const pay = d.payment || {};
  const totals = d.totals || {};
  const groups = Array.isArray(d.goods) ? d.goods : [];
  const parcels = Number(totals.parcels) || 0;
  const status = pay.status || "pending";
  const cur = pay.currency;
  const total = Number(pay.total) || 0;
  const paid = Number(pay.paid) || 0;
  const balance = Number(pay.balance) || 0;
  const showMoney = total > 0 && status !== "cancelled";
  const paidRatio = total > 0 ? Math.min(1, paid / total) : 0;
  const dayFmt = new Intl.DateTimeFormat(INTL_LOCALE[lang] || "en-GB", { day: "numeric", month: "long", year: "numeric" });
  const dueAt = pay.dueAt && balance > 0 ? dayFmt.format(new Date(`${pay.dueAt}T12:00:00Z`)) : null;

  const lines = [
    { k: m.freight, v: pay.freight },
    { k: m.customs, v: pay.customs, tag: pay.customsActual ? m.customsActual : m.customsEst },
    { k: m.goodsAdvance, v: pay.goodsAdvance },
    { k: m.other, v: pay.other },
  ].filter((l) => Number(l.v) > 0);

  // Shipment-level description stands in when parcels carry none (or none received yet).
  const rows = groups.length
    ? groups.map((g) => ({ ...g, description: g.description || (groups.length === 1 ? d.goodsDescription : null) }))
    : d.goodsDescription
    ? [{ description: d.goodsDescription, parcels: 0 }]
    : [];
  const visible = showAll ? rows : rows.slice(0, GROUPS_COLLAPSED);

  const stats = [
    { k: m.parcelsCount(parcels), show: parcels > 0, plain: true },
    { k: m.pieces, v: totals.pieces },
    { k: m.kg, v: totals.weight },
    { k: m.cbm, v: totals.cbm },
  ].filter((s) => s.show || Number(s.v) > 0);
  const num = new Intl.NumberFormat(INTL_LOCALE[lang] || "en-GB", { maximumFractionDigits: 2 });

  const receiverCity = localCity(d.consigneeCity, t.cities) || d.consigneeCity;
  const waText = encodeURIComponent(`${t.track.result.shipment} ${shipment.trackingNumber}`);

  return (
    <section ref={root} className="px-5 sm:px-8 pb-8" data-testid="track-details">
      {/* Who and what */}
      {(d.customerName || d.consigneeName) && (
        <div className="td-in grid sm:grid-cols-[1fr_auto_1fr] items-center gap-4 rounded-2xl bg-white/[0.04] border border-white/10 p-4 sm:p-5">
          {d.customerName ? <Person label={m.sender} name={d.customerName} /> : <span />}
          <ArrowRight className={`hidden sm:block w-5 h-5 text-[var(--signal)] ${isRtl ? "rotate-180" : ""}`} aria-hidden />
          {d.consigneeName ? <Person label={m.receiver} name={d.consigneeName} sub={receiverCity} /> : <span />}
        </div>
      )}

      <dl className="mt-4 flex flex-wrap gap-2">
        {[
          { k: m.reference, v: d.code, ltr: true },
          { k: m.kind, v: m.kinds[d.shipmentType] },
          { k: m.origin, v: d.countryOfOrigin },
        ]
          .filter((c) => c.v)
          .map((c) => (
            <div key={c.k} className="td-in inline-flex items-baseline gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-sm">
              <dt className="text-[var(--on-dark-muted)]">{c.k}</dt>
              <dd className="font-semibold text-[var(--on-dark)] type-num" dir={c.ltr ? "ltr" : undefined}>
                {c.v}
              </dd>
            </div>
          ))}
      </dl>

      <div className="mt-6 grid lg:grid-cols-12 gap-5">
        {/* Payment */}
        <div className="td-in lg:col-span-5 rounded-2xl bg-[oklch(0.24_0.032_140)] border border-white/10 p-5 sm:p-6 flex flex-col">
          <div className="flex items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 font-bold text-[var(--on-dark)]">
              <Wallet className="w-4 h-4 text-[var(--signal)]" aria-hidden /> {m.payment}
            </h3>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${STATUS_TONE[status] || STATUS_TONE.pending}`}
              data-testid="pay-status"
            >
              {status === "paid" ? <CircleCheck className="w-3.5 h-3.5" aria-hidden /> : <Clock className="w-3.5 h-3.5" aria-hidden />}
              {m.payStatus[status] || m.payStatus.pending}
            </span>
          </div>

          {showMoney ? (
            <>
              <div className="mt-5">
                <div className="text-xs text-[var(--on-dark-muted)]">{balance > 0 ? m.remaining : m.total}</div>
                <div
                  className={`mt-1 type-num text-3xl sm:text-4xl font-extrabold tracking-tight ${
                    balance > 0 ? DUE_TONE[status] || "text-[var(--on-dark)]" : "text-[var(--on-dark)]"
                  }`}
                  dir="ltr"
                  style={{ textAlign: isRtl ? "right" : "left" }}
                >
                  {money(balance > 0 ? balance : total, cur)}
                </div>
                {balance <= 0 && (
                  <div className="mt-1 flex items-center gap-1.5 text-sm text-[var(--signal)]">
                    <CircleCheck className="w-4 h-4" aria-hidden /> {m.allPaid}
                  </div>
                )}
              </div>

              <div className="mt-4">
                <div className="h-2 rounded-full bg-white/10 overflow-hidden" aria-hidden>
                  <div
                    className={`td-bar h-full rounded-full ${BAR_TONE[status] || "bg-[var(--signal)]"}`}
                    style={{ width: `${Math.round(paidRatio * 100)}%`, transformOrigin: isRtl ? "right" : "left" }}
                  />
                </div>
                <div className="mt-2 text-xs text-[var(--on-dark-muted)] type-num">{m.paidOf(iso(money(paid, cur)), iso(money(total, cur)))}</div>
              </div>

              <dl className="mt-5 space-y-2.5 text-sm">
                {lines.map((l) => (
                  <div key={l.k} className="flex items-baseline justify-between gap-3">
                    <dt className="text-[var(--on-dark-muted)]">
                      {l.k}
                      {l.tag && (
                        <span className="ms-1.5 rounded-md bg-white/[0.06] px-1.5 py-0.5 text-[11px] text-[var(--on-dark-muted)]">{l.tag}</span>
                      )}
                    </dt>
                    <dd className="type-num text-[var(--on-dark)]" dir="ltr">{money(l.v, cur)}</dd>
                  </div>
                ))}
                <div className="flex items-baseline justify-between gap-3 border-t border-white/10 pt-2.5 font-bold">
                  <dt className="text-[var(--on-dark)]">{m.total}</dt>
                  <dd className="type-num text-[var(--on-dark)]" dir="ltr">{money(total, cur)}</dd>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-[var(--on-dark-muted)]">{m.paid}</dt>
                  <dd className="type-num text-[var(--on-dark)]" dir="ltr">{money(paid, cur)}</dd>
                </div>
                {Number(pay.credit) > 0 && (
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-[var(--signal)]">{m.credit}</dt>
                    <dd className="type-num text-[var(--signal)]" dir="ltr">{money(pay.credit, cur)}</dd>
                  </div>
                )}
              </dl>

              {dueAt && (
                <div className="mt-4 flex items-center gap-2 text-sm text-[var(--on-dark-muted)]">
                  <CalendarClock className="w-4 h-4" aria-hidden /> {m.dueBy(dueAt)}
                </div>
              )}
            </>
          ) : (
            <p className="mt-5 text-sm leading-relaxed text-[var(--on-dark-muted)]">
              {status === "awaiting_confirmation" ? m.awaitingNote : status === "cancelled" ? d.cancelReason || "" : m.pendingNote}
            </p>
          )}

          {(balance > 0 || status === "awaiting_confirmation") && (
            <a
              href={`https://wa.me/${WHATSAPP}?text=${waText}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 lg:mt-auto lg:pt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--on-dark-muted)] hover:text-[var(--signal)] transition-colors"
            >
              <MessageCircle className="w-4 h-4" aria-hidden />
              {m.payHelp}
            </a>
          )}
        </div>

        {/* Contents */}
        <div className="td-in lg:col-span-7 rounded-2xl bg-[oklch(0.24_0.032_140)] border border-white/10 p-5 sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 font-bold text-[var(--on-dark)]">
              <Package className="w-4 h-4 text-[var(--signal)]" aria-hidden /> {m.contents}
            </h3>
            {parcels > 1 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--signal)] text-[var(--signal-ink)] px-3 py-1 text-xs font-bold" data-testid="multi-badge">
                <Boxes className="w-3.5 h-3.5" aria-hidden /> {m.parcelsCount(parcels)}
              </span>
            )}
          </div>

          {parcels > 1 && <p className="mt-3 text-sm text-[var(--on-dark-muted)]">{m.multiNote(parcels)}</p>}

          {stats.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-px rounded-xl overflow-hidden bg-white/10">
              {stats.map((s) => (
                <div key={s.k} className="bg-[oklch(0.215_0.03_140)] px-3 py-2.5">
                  {s.plain ? (
                    <div className="font-bold text-[var(--on-dark)]">{s.k}</div>
                  ) : (
                    <div className="type-num font-bold text-[var(--on-dark)]">
                      {num.format(Number(s.v))} <span className="text-xs font-semibold text-[var(--on-dark-muted)]">{s.k}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {rows.length > 0 && (
            <ul className="mt-4 divide-y divide-white/[0.07]" data-testid="goods-list">
              {visible.map((g, i) => {
                const n = Number(g.parcels) || 0;
                const done = Number(g.delivered) || 0;
                const facts = [
                  Number(g.pieces) > 0 && `${num.format(g.pieces)} ${m.pieces}`,
                  Number(g.weight) > 0 && `${num.format(g.weight)} ${m.kg}`,
                ].filter(Boolean);
                return (
                  <li key={`${g.description}-${i}`} className="flex items-center gap-3 py-3">
                    <span className="shrink-0 min-w-11 h-11 px-2 rounded-xl grid place-items-center bg-white/[0.06] border border-white/10 type-num font-extrabold text-[var(--on-dark)]">
                      {n > 0 ? `×${n}` : <Package className="w-4 h-4 text-[var(--on-dark-muted)]" aria-hidden />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[var(--on-dark)] break-words">{g.description || m.unspecified}</div>
                      {(facts.length > 0 || n > 0) && (
                        <div className="mt-0.5 text-xs text-[var(--on-dark-muted)] type-num">
                          {[n > 0 && m.parcelsCount(n), ...facts].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                    {n > 1 && done > 0 && done < n && (
                      <span className="shrink-0 text-xs text-[var(--signal)] type-num">{m.delivered(done, n)}</span>
                    )}
                    {n > 0 && done === n && <CircleCheck className="shrink-0 w-4 h-4 text-[var(--signal)]" aria-label={m.delivered(done, n)} />}
                  </li>
                );
              })}
            </ul>
          )}

          {rows.length > GROUPS_COLLAPSED && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              aria-expanded={showAll}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--signal)] hover:underline underline-offset-4"
            >
              {showAll ? m.showLess : m.showAll(rows.length)}
              <ChevronDown className={`w-4 h-4 transition-transform ${showAll ? "rotate-180" : ""}`} aria-hidden />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
